import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { fetchCsrfToken, submitContactMessage } from '../../api/contact';
import './Contact.css';

const INQUIRY_TYPES = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'volunteer', label: 'Volunteer Opportunity' },
  { value: 'donation', label: 'Donation Information' },
  { value: 'partnership', label: 'Partnership Opportunity' },
  { value: 'education', label: 'Educational Resources' },
  { value: 'report', label: 'Report Beetle Damage' },
  { value: 'media', label: 'Media Inquiry' },
  { value: 'other', label: 'Other' },
];

const initialFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  organization: '',
  inquiry_type: 'general',
  subject: '',
  message: '',
};

// Human-readable labels for the missing-fields summary.
const FIELD_LABELS = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email Address',
  subject: 'Subject',
  message: 'Message',
};

function Contact() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [submitAttemptCount, setSubmitAttemptCount] = useState(0);
  const errorSummaryRef = useRef(null);

  useEffect(() => {
    fetchCsrfToken().catch(console.error);
  }, []);

  useEffect(() => {
    if (!showSuccessModal) return;

    setCountdown(3);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showSuccessModal, navigate]);

  // After a failed submit, bring the missing-fields summary into view.
  useEffect(() => {
    if (submitAttemptCount > 0 && errorSummaryRef.current) {
      errorSummaryRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submitAttemptCount]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Scroll to and focus a field when its summary link is clicked.
  const focusField = (fieldName) => {
    const el = document.querySelector(`[name="${fieldName}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus({ preventScroll: true });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setSubmitAttemptCount((count) => count + 1);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      await submitContactMessage(formData);
      setShowSuccessModal(true);
    } catch (error) {
      setSubmitError(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const errorKeys = Object.keys(errors).filter((key) => errors[key]);

  return (
    <>
      <Modal show={showSuccessModal} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center py-5">
          <div className="text-primary mb-3">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
          </div>
          <h3 className="mb-3">Message Sent Successfully!</h3>
          <p className="text-muted mb-2">
            Thank you for reaching out. We will review your message and get back to you as soon as possible.
          </p>
          <p className="text-muted small">
            Redirecting to home in {countdown} second{countdown !== 1 ? 's' : ''}...
          </p>
        </Modal.Body>
      </Modal>

    <div className="contact-page">
      <Helmet>
        <title>Contact Us | Mountain Pine Beetle Foundation</title>
        <meta name="description" content="Contact Mountain Pine Beetle Foundation to report beetle damage, volunteer for forest conservation, learn about donations, or inquire about our prevention and education programs." />
        <meta name="keywords" content="contact mountain pine beetle prevention, report beetle damage, report pine beetle infestation, volunteer forest conservation, donate tree protection, beetle prevention inquiry, forest health nonprofit contact" />
        <link rel="canonical" href="https://www.mountainpinebeetle.org/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.mountainpinebeetle.org/contact" />
        <meta property="og:title" content="Contact Us | Mountain Pine Beetle Foundation" />
        <meta property="og:description" content="Contact Mountain Pine Beetle Foundation to report beetle damage, volunteer, or learn about our forest conservation programs." />
        <meta property="og:site_name" content="Mountain Pine Beetle Foundation" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:image" content="https://www.mountainpinebeetle.org/mpb_logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Us | Mountain Pine Beetle Foundation" />
        <meta name="twitter:description" content="Contact Mountain Pine Beetle Foundation to report beetle damage, volunteer, or learn about our forest conservation programs." />
        <meta name="twitter:image" content="https://www.mountainpinebeetle.org/mpb_logo.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Mountain Pine Beetle Foundation",
            "description": "Contact us to report beetle damage, volunteer, donate, or learn about our prevention programs.",
            "url": "https://www.mountainpinebeetle.org/contact",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Mountain Pine Beetle Foundation",
              "url": "https://www.mountainpinebeetle.org"
            }
          }
        `}</script>
      </Helmet>

      <section className="contact-header py-5">
        <Container>
          <div className="text-center py-3">
            <h1 className="mb-3">Get in Touch</h1>
            <p className="mx-auto mb-0" style={{ maxWidth: '520px' }}>
              Report beetle damage, volunteer for conservation, or learn about our prevention programs.
            </p>
          </div>
        </Container>
      </section>

    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card className="contact-form-card">
            <Card.Body>

                  {errorKeys.length > 0 && (
                    <Alert variant="danger" className="mb-4" ref={errorSummaryRef}>
                      <div className="d-flex align-items-center fw-bold mb-2">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="me-2 flex-shrink-0" aria-hidden="true">
                          <path d="M12 2L1 21h22L12 2zm0 4l7.53 13H4.47L12 6zm-1 5v4h2v-4h-2zm0 6v2h2v-2h-2z" />
                        </svg>
                        <span>
                          Your message isn't ready to send yet — please complete the {errorKeys.length}{' '}
                          {errorKeys.length === 1 ? 'item' : 'items'} below:
                        </span>
                      </div>
                      <ul className="mb-0 ps-4">
                        {errorKeys.map((key) => (
                          <li key={key}>
                            <Alert.Link
                              as="button"
                              type="button"
                              className="p-0 border-0 bg-transparent align-baseline text-decoration-underline"
                              onClick={() => focusField(key)}
                            >
                              {FIELD_LABELS[key] || key}
                            </Alert.Link>
                            {' — '}
                            {errors[key]}
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {submitError && (
                    <Alert variant="danger" dismissible onClose={() => setSubmitError(null)}>
                      {submitError}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            isInvalid={!!errors.first_name}
                            placeholder="Enter your first name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.first_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            isInvalid={!!errors.last_name}
                            placeholder="Enter your last name"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.last_name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            isInvalid={!!errors.email}
                            placeholder="Enter your email"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="Enter your phone number"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Organization</Form.Label>
                          <Form.Control
                            type="text"
                            name="organization"
                            value={formData.organization}
                            onChange={handleChange}
                            placeholder="Enter your organization (optional)"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Inquiry Type</Form.Label>
                          <Form.Select
                            name="inquiry_type"
                            value={formData.inquiry_type}
                            onChange={handleChange}
                          >
                            {INQUIRY_TYPES.map((type) => (
                              <option key={type.value} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Subject <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        isInvalid={!!errors.subject}
                        placeholder="Enter the subject of your message"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.subject}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Message <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        isInvalid={!!errors.message}
                        placeholder="Enter your message"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.message}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <div className="d-grid d-md-block">
                      <Button
                        variant="primary"
                        type="submit"
                        size="lg"
                        disabled={isSubmitting}
                        className="px-5"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </div>

                    <p className="text-muted small mt-3 mb-0">
                      By clicking <strong>Send Message</strong>, you agree to the Foundation&rsquo;s data-handling terms:
                      the information you submit (your message and contact details) may be transmitted to and processed by
                      third-party service providers &mdash; including large language model (LLM) and other AI services
                      &mdash; for intake, categorization, drafting, and communications on behalf of the Foundation. The
                      Foundation will use commercially reasonable efforts to select providers with appropriate
                      data-handling, security, and privacy safeguards, and will not sell your personal information. If you
                      do not agree, please do not submit this form. See our <Link to="/privacy">Privacy Policy</Link>.
                    </p>
                  </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </div>
    </>
  );
}

export default Contact;
